import { getTaskSyncStatus, syncPodcast } from "@/api/podcasts.api"
import { notificationError, notificationSuccess } from "@/common/helpers/notifications.helper"
import { STORAGE_KEYS } from "@/auth/session"
import { i18n } from "@/plugins/i18n"

const POLL_INTERVAL = 2000
const MAX_POLL_ERRORS = 5

const timers = new Map()

const readStored = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEYS.PODCAST_IMPORT_REFRESHES))

    return stored && typeof stored === "object" ? stored : {}
  } catch {
    return {}
  }
}

const podcastName = (title) => {
  const trimmed = title?.trim()

  return trimmed ? `"${trimmed}"` : i18n.t("podcasts.import_source.this_podcast")
}

const stopTimer = (podcastKey) => {
  clearTimeout(timers.get(podcastKey))
  timers.delete(podcastKey)
}

export const persist = ({ state }) => {
  try {
    if (Object.keys(state.refreshes).length) {
      localStorage.setItem(STORAGE_KEYS.PODCAST_IMPORT_REFRESHES, JSON.stringify(state.refreshes))
    } else {
      localStorage.removeItem(STORAGE_KEYS.PODCAST_IMPORT_REFRESHES)
    }
  } catch (e) {
    console.error(e)
  }
}

export const startRefresh = async ({ commit, dispatch, getters }, { accountKey, podcastKey, title }) => {
  if (getters.isRefreshing(podcastKey)) return

  const { id: taskId } = await syncPodcast(accountKey, podcastKey)

  if (!taskId) throw new Error("The sync request returned no task id")

  commit("setRefresh", { podcastKey, accountKey, taskId, title: title ?? "", startedAt: Date.now() })
  dispatch("persist")
  dispatch("pollRefresh", { podcastKey })
}

export const pollRefresh = ({ dispatch, getters }, { podcastKey, errors = 0 }) => {
  stopTimer(podcastKey)

  timers.set(
    podcastKey,
    setTimeout(async () => {
      timers.delete(podcastKey)

      const refresh = getters.refreshFor(podcastKey)

      if (!refresh) return

      let task

      try {
        task = await getTaskSyncStatus(refresh.accountKey, refresh.taskId)
      } catch (e) {
        if (getters.refreshFor(podcastKey)?.taskId !== refresh.taskId) return

        if (e?.response?.status === 404) {
          dispatch("finishRefresh", { podcastKey, outcome: "EXPIRED" })
        } else if (errors + 1 >= MAX_POLL_ERRORS) {
          console.error(e)
          dispatch("finishRefresh", { podcastKey, outcome: "LOST" })
        } else {
          dispatch("pollRefresh", { podcastKey, errors: errors + 1 })
        }

        return
      }

      if (getters.refreshFor(podcastKey)?.taskId !== refresh.taskId) return

      const status = task?.status?.toUpperCase()

      if (status === "PROCESSING") {
        dispatch("pollRefresh", { podcastKey })
      } else {
        dispatch("finishRefresh", { podcastKey, outcome: status, message: task?.message })
      }
    }, POLL_INTERVAL)
  )
}

export const finishRefresh = async ({ commit, dispatch, getters, rootGetters }, { podcastKey, outcome, message }) => {
  const refresh = getters.refreshFor(podcastKey)

  if (!refresh) return

  stopTimer(podcastKey)
  commit("removeRefresh", podcastKey)
  dispatch("persist")

  const name = podcastName(refresh.title)

  switch (outcome) {
    case "SUCCESS":
      notificationSuccess(
        i18n.t("podcasts.import_source.success", { name }),
        i18n.t("podcasts.sync_success_title")
      )
      break
    case "NO_CHANGES":
      notificationSuccess(
        i18n.t("podcasts.import_source.no_changes", { name }),
        i18n.t("podcasts.sync_success_title")
      )
      break
    case "EXPIRED":
      break
    case "LOST":
      notificationError(i18n.t("podcasts.import_source.lost", { name }), i18n.t("podcasts.sync_error_title"))
      break
    default:
      notificationError(
        message || i18n.t("podcasts.import_source.failed", { name }),
        i18n.t("podcasts.sync_error_title")
      )
  }

  if (rootGetters["Podcast/podcast"]?.key === podcastKey) {
    try {
      await dispatch("Podcast/getPodcast", podcastKey, { root: true })
    } catch (e) {
      console.error(e)
    }
  }
}

export const resumeRefreshes = ({ commit, dispatch, getters }) => {
  Object.values(readStored()).forEach((refresh) => {
    if (!refresh?.podcastKey || !refresh.taskId || getters.isRefreshing(refresh.podcastKey)) return

    commit("setRefresh", refresh)
    dispatch("pollRefresh", { podcastKey: refresh.podcastKey })
  })
}

export const cancelAllRefreshes = ({ commit, state }) => {
  Object.keys(state.refreshes).forEach(stopTimer)
  commit("reset")
}
